import React, { useState, useEffect, useRef } from "react";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import { styled } from "@mui/system";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Box from "@mui/material/Box";
import { useSelector } from "react-redux";
import { brandColour } from "@tphglobal/common/theme/style.palette";
import moment from "moment";
import { ReduxState } from "../../../redux/reducers";
import { DailyTime, Payas, TimeOption } from "../../../models";
import { TextInput } from "@tphglobal/components";

interface Props {
  setWeekData?: (update: (weekData: DailyTime[]) => DailyTime[]) => void;
  fieldKey?: keyof DailyTime;
  rowIndex?: number;
  value?: string;
  timeOption?: string;
  isTimeSheet?: boolean;
  onChange?: any;
  label?: string;
  disabled?: boolean;
  payAs?: Payas[] | null;
}

const TimePickerDropdown = styled("div")({
  border: "1px solid #ccc",
  background: "white",
  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
  maxHeight: "200px",
  overflowY: "auto",
  padding: "5px 10px",
  boxSizing: "border-box",
});

const TextTimeInput = styled("input")<{ isTimeSheet?: boolean }>`
  ${({ isTimeSheet }) =>
    !isTimeSheet &&
    `
    &::-webkit-calendar-picker-indicator {
      display: none;
    }
    border-radius: 4px;
    background: transparent; 
    border: none; 
    max-width: 50px;
  `}
`;

const StyledMenuItem = styled(MenuItem)({
  padding: "5px 10px",
});

const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));

const timeInputField: React.FC<Props> = ({
  setWeekData,
  fieldKey,
  rowIndex,
  value = "00:00",
  timeOption,
  isTimeSheet = false,
  onChange,
  label,
  disabled,
  payAs,
}) => {
  const alertTimeout = useRef<NodeJS.Timeout | null>(null);
  const userProfile = useSelector((state: ReduxState) => state?.profile);
  const minutesStep = userProfile?.MinutsStep > 0 ? userProfile?.MinutsStep : 5;

  const minutes = Array.from({ length: 60 / minutesStep }, (_, i) =>
    String(i * minutesStep).padStart(2, "0"),
  );

  const [open, setOpen] = useState(false);
  const [typeFieldInput, setTypeFieldInput] = useState<string>(
    value || "00:00",
  );

  const [selectedHour, setSelectedHour] = useState<string>(
    value?.split(":")[0],
  );
  const [selectedMinute, setSelectedMinute] = useState<string>(
    value?.split(":")[1],
  );

  // Refs & state for Popover anchor
  const anchorRef = useRef<HTMLSpanElement | null>(null);

  const calculateTotalHours = (
    startTime: string,
    finishTime: string,
    totalMBDed: string,
    travel1: string,
    travel2: string,
  ) => {
    const start = moment(startTime, "HH:mm");
    const finish = moment(finishTime, "HH:mm");

    const totalMBMinutes = moment.duration(totalMBDed).asMinutes();
    const travel1Minutes = moment.duration(travel1 || "00:00").asMinutes();
    const travel2Minutes = moment.duration(travel2 || "00:00").asMinutes();

    if (finish.isBefore(start)) {
      finish.add(1, "day");
    }

    let durationMinutes = moment.duration(finish.diff(start)).asMinutes();

    durationMinutes -= totalMBMinutes;
    durationMinutes += travel1Minutes;
    durationMinutes += travel2Minutes;

    const isNegative = durationMinutes < 0;

    const absoluteDurationMinutes = Math.abs(durationMinutes);

    const hours = Math.floor(absoluteDurationMinutes / 60);
    const minutes = Math.floor(absoluteDurationMinutes % 60);

    const formattedHours = String(hours).padStart(2, "0");
    const formattedMinutes = String(minutes).padStart(2, "0");

    return isNegative
      ? `-${formattedHours}:${formattedMinutes}`
      : `${formattedHours}:${formattedMinutes}`;
  };

  useEffect(() => {
    const safeValue = value && value !== "" ? value : "00:00";

    setSelectedHour(safeValue.split(":")[0]);
    setSelectedMinute(safeValue.split(":")[1]);
    setTypeFieldInput(safeValue);
  }, [value]);

  const handleSelectHour = (hour: string, minute?: string) => {
    setSelectedHour(hour);
    setWeekData &&
      setWeekData((prevWeekData: DailyTime[]) => {
        const newWeekData: DailyTime[] = [...prevWeekData];
        const time = `${hour}:${minute || selectedMinute}`;

        if (newWeekData[rowIndex]) {
          const {
            Start,
            Finish,
            TotalMBDed,
            Travel1 = "00:00",
            Travel2 = "00:00",
          } = newWeekData[rowIndex];

          let updatedStart = Start;
          let updatedFinish = Finish;
          let updatedTotalMBDed = TotalMBDed;
          let updatedTravel1 = Travel1;
          let updatedTravel2 = Travel2;

          if (fieldKey === "Start") {
            updatedStart = time;
          } else if (fieldKey === "Finish") {
            updatedFinish = time;
          } else if (fieldKey === "TotalMBDed") {
            updatedTotalMBDed = time;
          } else if (fieldKey === "Travel1") {
            updatedTravel1 = time;
          } else if (fieldKey === "Travel2") {
            updatedTravel2 = time;
          }

          const totalHours = calculateTotalHours(
            updatedStart,
            updatedFinish,
            updatedTotalMBDed,
            updatedTravel1,
            updatedTravel2,
          );

          newWeekData[rowIndex] = {
            ...newWeekData[rowIndex],
            [fieldKey]: time,
            TotalHours: totalHours,
          };

          if (
            newWeekData[rowIndex]?.Payas === "0" &&
            payAs?.find((p) => p.Value.toLowerCase() === "worked")
          ) {
            newWeekData[rowIndex] = {
              ...newWeekData[rowIndex],
              Payas: "Worked",
              customNote: "Worked ",
            };
          }
        }

        return newWeekData;
      });
  };

  const handleSelectMinute = (minute: string, hour?: string) => {
    setSelectedMinute(minute);
    setWeekData &&
      setWeekData((prevWeekData: DailyTime[]) => {
        const newWeekData: DailyTime[] = [...prevWeekData];
        const time = `${hour || selectedHour}:${minute}`;

        if (newWeekData[rowIndex]) {
          const {
            Start,
            Finish,
            TotalMBDed,
            Travel1 = "00:00",
            Travel2 = "00:00",
          } = newWeekData[rowIndex];

          let updatedStart = Start;
          let updatedFinish = Finish;
          let updatedTotalMBDed = TotalMBDed;
          let updatedTravel1 = Travel1;
          let updatedTravel2 = Travel2;

          if (fieldKey === "Start") {
            updatedStart = time;
          } else if (fieldKey === "Finish") {
            updatedFinish = time;
          } else if (fieldKey === "TotalMBDed") {
            updatedTotalMBDed = time;
          } else if (fieldKey === "Travel1") {
            updatedTravel1 = time;
          } else if (fieldKey === "Travel2") {
            updatedTravel2 = time;
          }

          const totalHours = calculateTotalHours(
            updatedStart,
            updatedFinish,
            updatedTotalMBDed,
            updatedTravel1,
            updatedTravel2,
          );

          newWeekData[rowIndex] = {
            ...newWeekData[rowIndex],
            [fieldKey]: time,
            TotalHours: totalHours,
          };
        }

        return newWeekData;
      });
  };

  const handleClickAway = () => {
    setOpen(false);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let valueLocal: any = event?.target?.value;

    if (typeof event === "string") {
      valueLocal = event;
    }

    setTypeFieldInput(valueLocal);

    if (valueLocal === "") return;

    let isValid = true;
    if (valueLocal.length > 5) {
      isValid = false;
    } else {
      let timeParts = valueLocal.split(/[:,.]/);
      if (timeParts.length > 2) {
        isValid = false;
      } else if (
        timeParts.length === 1 &&
        Number(valueLocal) >= 0 &&
        Number(valueLocal) <= 23
      ) {
        valueLocal = ("00" + valueLocal).slice(-2);
        valueLocal = valueLocal + ":00";
      } else if (timeParts.length === 1) {
        valueLocal = ("0000" + valueLocal).slice(-4);
        valueLocal =
          valueLocal.substring(0, 2) +
          ":" +
          valueLocal.substring(2, valueLocal.length);
      } else {
        valueLocal =
          ("00" + timeParts[0]).slice(-2) +
          ":" +
          ("00" + timeParts[1]).slice(-2);
      }
    }

    if (alertTimeout.current) {
      clearTimeout(alertTimeout.current);
    }

    alertTimeout.current = setTimeout(() => {
      if (!moment(valueLocal, "HH:mm", true).isValid() || !isValid) {
        setSelectedHour("00");
        setSelectedMinute("00");
        setTypeFieldInput("");
        alert("Time string is not valid.");
        return;
      } else {
        if (onChange && isTimeSheet) {
          onChange(moment(valueLocal, "HH:mm"));
        }
        setTypeFieldInput(valueLocal);
        const formattedTime = moment(valueLocal, "HH:mm").format("HH:mm");
        const [hour, minute] = formattedTime.split(":");

        setSelectedHour((prevHour: string) => {
          if (prevHour !== hour) {
            handleSelectHour(hour, minute);
          }
          return hour;
        });
        setSelectedMinute((prevMinute: string) => {
          if (prevMinute !== minute) {
            handleSelectMinute(minute, hour);
          }
          return minute;
        });
      }
    }, 1000);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();

      const nextRow = (rowIndex ?? 0) + 1;
      const nextInput = document.querySelector(
        `[data-row="${nextRow}"][data-field="${fieldKey}"]`,
      ) as HTMLInputElement;

      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  const RenderTextInput = () => {
    if (isTimeSheet) {
      return (
        <TextInput
          value={typeFieldInput}
          onChange={handleInputChange}
          label={label}
          onKeyDown={handleKeyDown}
          inputProps={{
            "data-row": rowIndex,
            "data-field": fieldKey,
          }}
        />
      );
    } else {
      return (
        <TextTimeInput
          isTimeSheet={isTimeSheet}
          type="text"
          tabIndex={disabled ? -1 : 0}
          onFocus={(e) => {
            if (disabled) e.target.blur();
          }}
          value={typeFieldInput}
          onChange={handleInputChange}
          placeholder="00:00"
          onKeyDown={handleKeyDown}
          data-row={rowIndex}
          data-field={fieldKey}
        />
      );
    }
  };

  const handleTriggerClick = (e?: React.MouseEvent) => {
    setOpen((prev) => {
      const next = !prev;
      return next;
    });
  };

  const popoverContent = (
    <Box sx={{ display: "flex", gap: 1 }}>
      <TimePickerDropdown>
        {hours.map((hour) => (
          <StyledMenuItem
            key={hour}
            onClick={
              !disabled
                ? () => {
                    handleSelectHour(hour);
                  }
                : undefined
            }
          >
            {hour}
          </StyledMenuItem>
        ))}
      </TimePickerDropdown>
      <TimePickerDropdown>
        {minutes.map((minute) => (
          <StyledMenuItem
            key={minute}
            onClick={
              !disabled
                ? () => {
                    handleSelectMinute(minute);
                  }
                : undefined
            }
          >
            {minute}
          </StyledMenuItem>
        ))}
      </TimePickerDropdown>
    </Box>
  );

  if (timeOption === TimeOption.DropList) {
    return (
      <>
        <Box
          position="relative"
          display="inline-block"
          width={"100%"}
          sx={{
            pointerEvents: disabled ? "none" : "auto",
            opacity: disabled ? 0.5 : 1,
          }}
        >
          <Box
            component="span"
            ref={anchorRef}
            onClick={!disabled ? handleTriggerClick : () => {}}
            sx={{
              cursor: disabled ? "not-allowed" : "pointer",
              borderRadius: "4px",
              backgroundColor: open ? brandColour?.primary20 : "white",
              display: "inline-block",
              padding: "2px 4px",
            }}
          >
            {`${selectedHour}:${selectedMinute}`}
          </Box>

          <Popover
            open={open}
            anchorEl={anchorRef.current}
            onClose={() => setOpen(false)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                p: 0,
                mt: "4px",
                overflow: "visible",
                maxHeight: 220,
                boxSizing: "border-box",
              },
            }}
          >
            {popoverContent}
          </Popover>
        </Box>
      </>
    );
  }

  return (
    <ClickAwayListener onClickAway={!disabled ? handleClickAway : () => {}}>
      <Box
        position="relative"
        display="inline-block"
        width={"100%"}
        sx={{
          pointerEvents: disabled ? "none" : "auto", // Disable interactions
          opacity: disabled ? 0.5 : 1, // Visually indicate disabled state
        }}
      >
        {timeOption === TimeOption.DropList ? (
          <Box
            component="span"
            onClick={!disabled ? handleTriggerClick : () => {}}
            sx={{
              cursor: disabled ? "not-allowed" : "pointer",
              borderRadius: "4px",
              backgroundColor: open ? brandColour?.primary20 : "white",
            }}
          >
            {`${selectedHour}:${selectedMinute}`}
          </Box>
        ) : (
          RenderTextInput()
        )}
        {!disabled && open && timeOption === TimeOption.DropList && (
          <Box
            display="flex"
            position="absolute"
            top="100%"
            left="0"
            zIndex={100000}
          >
            <TimePickerDropdown>
              {hours.map((hour) => (
                <StyledMenuItem
                  key={hour}
                  onClick={!disabled ? () => handleSelectHour(hour) : () => {}}
                >
                  {hour}
                </StyledMenuItem>
              ))}
            </TimePickerDropdown>
            <TimePickerDropdown>
              {minutes.map((minute) => (
                <StyledMenuItem
                  key={minute}
                  onClick={
                    !disabled ? () => handleSelectMinute(minute) : () => {}
                  }
                >
                  {minute}
                </StyledMenuItem>
              ))}
            </TimePickerDropdown>
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default timeInputField;
